'use client'
import { FC, ReactNode, useMemo } from 'react'
import { MdClose, MdOutlineMenu } from 'react-icons/md'

import { useModal, useSlideOut } from '#/src/custom/controllers'
import { RequiredPageProps } from 'ecosystem'
import type { TopMenuNavItem } from 'api'
import { useAppConfigContext } from '#/src/appConfig'
import { TopNavSpacer } from 'ui/storefront'
import { MenuItem } from './custom-layout-components/TopNav/MenuItem'
import { TopNav } from './custom-layout-components/TopNav/TopNav'
import CustomFooter from '#/src/custom/layout/CustomFooter'
import { Box } from '@chakra-ui/react'
import { PageLayoutContainer } from 'storefront-modules/page'
import { TopNavLogo } from '#/src/custom/layout/TopNavLogo'

export type CustomLayoutProps = RequiredPageProps & {
  children: ReactNode | ReactNode[]
  topMenu?: TopMenuNavItem[]
}

const CustomLayout: FC<CustomLayoutProps> = ({
  children,
  footerWidgets,
  staticPages,
  categories,
  topMenu
}) => {
  const appConfig = useAppConfigContext()
  const { isHidden, closeSlideOut, openSlideOut, componentId } = useSlideOut()
  const { isHidden: isModalHidden, size: modalSize } = useModal()

  // Granditude navigation links
  const staticLinks = useMemo(() => {
    return appConfig.staticLinks.length > 1
      ? appConfig.staticLinks
      : [
          { id: 'what-we-do', label: 'What We Do', path: '/what-we-do' },
          { id: 'partners', label: 'Partners & Integrations', path: '/partners' },
          { id: 'working-together', label: 'Working Together', path: '/working-together' }
        ]
  }, [appConfig.staticLinks])

  const menuItems: {
    other: MenuItem[]
  } = useMemo(() => {
    // a menu built in SFA (TOP_MENU view) replaces the static links entirely
    if (topMenu?.length) {
      return {
        other: topMenu.map(({ id, label, path, isExternal, subItems }) => ({
          id: 'top-menu-' + id,
          text: label,
          path,
          isExternal,
          subItems: subItems.map(({ label: text, ...sub }) => ({ ...sub, text })),
          onHoverEnter: () => closeSlideOut()
        }))
      }
    }

    return {
      other: staticLinks.map(({ label, ...s }) => ({
        ...s,
        text: label,
        onHoverEnter: () => closeSlideOut()
      }))
    }
  }, [closeSlideOut, staticLinks, topMenu])

  const footerItems = useMemo(() => footerWidgets.map(({ widget }) => widget), [footerWidgets])

  const logoComponent = <TopNavLogo />

  const handleOnMenuToggle = (menuItems: MenuItem[]) => {
    if (isHidden) {
      openSlideOut({
        title: '',
        componentId: 'mobileMenu',
        footerId: 'mobileMenu',
        extraArgs: {
          menuItems
        },
        drawerProps: {
          sx: {
            '.GrandHeading': {
              textTransform: 'none !important'
            }
          }
        }
      })
    } else {
      closeSlideOut()
    }
  }

  const handleOnMenuHover = (isInSide: boolean) => {
    if (isInSide && componentId === 'categories') {
      closeSlideOut()
    }
  }

  return (
    <PageLayoutContainer
      pageContextProps={{
        categories: categories || [],
        footerWidgets,
        staticPages
      }}
      position="relative"
    >
      <TopNav
        contentHeight="8vh"
        logo={logoComponent}
        onMenuHover={handleOnMenuHover}
        onMenuToggle={handleOnMenuToggle}
        toggleIcon={
          isHidden ? (
            <Box color="text.default">
              <MdOutlineMenu />
            </Box>
          ) : (
            <MdClose />
          )
        }
        zIndex={!isModalHidden && modalSize === 'full' ? 0 : 9000}
        {...{ menuItems, isHidden }}
      />
      <TopNavSpacer h="8vh" />
      {children}
      <CustomFooter footerItems={footerItems} />
    </PageLayoutContainer>
  )
}

export default CustomLayout
